// Global imports
import { memo } from 'react'
import { Button } from '~/components'
import { formatDate } from '~/utils'
import {
  checkBrendaPickupPointStock,
  formatBrendaBranchDistance,
} from '~/utils/brendaStock'

// Types
import type {
  BrendaChargesExtra,
  BrendaChargesPickupPoint,
} from '~/types/brendaCharges'

interface Props {
  point: BrendaChargesPickupPoint
  itemQuantity: number
  addOns: BrendaChargesExtra[]
  /**
   * Stock codes of every linked child currently in the user's
   * basket under this parent line — mandatory and non-mandatory
   * alike. Passed to `checkBrendaPickupPointStock` so the branch's
   * "available from" date accounts for any child whose own
   * `firstAvailableDate` lags the parent.
   */
  basketLinkedStockCodes: string[]
  isActive: boolean
  /**
   * The start date the pickup search was anchored on. Echoed back by
   * `checkBrendaPickupPointStock` as the "Available from" date for
   * branches with current stock — a branch's own
   * `firstAvailableDate` takes precedence when it has future stock.
   */
  requestedStartDate: string
  onSelect: (point: BrendaChargesPickupPoint) => void
}

/**
 * Displays a single pickup branch row in the pickup modal.
 *
 * All availability + date logic lives in
 * `checkBrendaPickupPointStock` so the card is purely
 * presentational: it calls the helper once, destructures the result,
 * and renders whichever date the helper says this branch will
 * actually be available on.
 *
 *   - `inStock: true`, `futureDate: null` → current stock; display
 *     `requestedDate` (the anchor) + the branch's own live quantity.
 *   - `inStock: true`, `futureDate: <date>` → future stock; display
 *     the branch's `firstAvailableDate` instead.
 *   - `inStock: false` → display "Not available", disable select.
 */
const PickupBranchCard = ({
  point,
  itemQuantity,
  addOns,
  basketLinkedStockCodes,
  isActive,
  requestedStartDate,
  onSelect,
}: Props) => {
  const { inStock, requestedDate, futureDate } = checkBrendaPickupPointStock(
    point,
    itemQuantity || 1,
    requestedStartDate,
    addOns,
    basketLinkedStockCodes
  )

  const canSelect = !isActive && inStock

  // The date this branch is available on. `futureDate` accounts for both
  // a future-stock branch and a linked add-on that isn't in stock here
  // (bumped to when it becomes available); a plain current-stock branch
  // with all add-ons in stock uses the requested anchor.
  const availableDate = inStock ? (futureDate ?? requestedDate) : null

  return (
    <div className="mb-3 grid grid-cols-1 rounded-lg bg-neutral-100 p-5 md:grid-cols-3 md:gap-5">
      <div className="grid grid-cols-1 md:col-span-2 md:grid-cols-2">
        <div className="pr-5">
          <p className="mb-0.5 text-sm font-semibold">
            {point.customerSupplierName}
          </p>
          <p className="mb-0.5 text-sm">{point.siteLocation?.address1}</p>
          <p className="mb-0.5 text-sm">{point.siteLocation?.city}</p>
          <p className="mb-2 text-sm">{point.siteLocation?.postcode}</p>
          <p className="mb-0 text-xs text-neutral-500">
            {formatBrendaBranchDistance(point.distance)}&nbsp;away
          </p>
        </div>
        <div className="mt-5 border-t border-neutral-200 pt-5 md:mt-0 md:border-l md:border-t-0 md:pl-5 md:pt-0">
          {availableDate ? (
            <>
              <p className="mb-1 text-sm">
                Available from&nbsp;
                <span className="font-semibold text-teal-green">
                  {formatDate(availableDate)}
                </span>
              </p>
              <p className="mb-0 text-sm font-semibold">
                {futureDate
                  ? 'Stock available'
                  : point.quantity > 0 && point.quantity <= 10
                    ? point.quantity + ' in stock'
                    : point.quantity > 10
                      ? 'In stock'
                      : 'Out of stock'}
              </p>
            </>
          ) : null}
        </div>
      </div>
      <div className="pt-8 text-center md:pt-0 md:text-right">
        {canSelect ? (
          <Button
            onClick={() => onSelect(point)}
            title="Select branch"
            style="tealGreen"
          />
        ) : isActive ? (
          <p className="mb-0 inline-block w-auto px-4 py-2 font-semibold text-teal-green md:py-3">
            Selected branch
          </p>
        ) : (
          <p className="mb-0 inline-block px-4 py-2 font-semibold text-red-500 md:py-3">
            Not available
          </p>
        )}
      </div>
    </div>
  )
}

export default memo(PickupBranchCard)
